// src/components/RichTextEditor.js
// Rich text editor with predefined sections + free text.
// Uses contentEditable + execCommand for basic formatting.
// Exports plain text and HTML for PDF rendering.
import React, { useRef, useState } from 'react';

const PREDEFINED_SECTIONS = {
  en: [
    {
      key: 'methods_purity',
      label: 'Purity / Potency / Identification',
      standard: 'USP <621>',
      text: `2 mL of purified water is added to the lyophilized powder in the vial, and the contents are gently mixed until fully dissolved.
An aliquot is taken from the vial and diluted to contain approximately 500 mg/L of the compound.
The diluted sample is analyzed by HPLC-UV/VIS using a validated analytical method.
The total area of all peaks in the chromatogram is calculated. The area of the main compound peak is divided by the total chromatographic peak area to obtain the chromatographic purity value, reported as a percentage.
Identification is confirmed by retention time comparison against a qualified reference standard and/or by molecular weight confirmation when applicable.`,
    },
    {
      key: 'methods_endotoxins',
      label: 'Endotoxins',
      standard: 'USP <85>',
      text: `2 mL of endotoxin-free water is added to the lyophilized powder in the vial, and the contents are gently mixed until fully dissolved.
An aliquot is taken from the vial and diluted with endotoxin-free water.
The diluted sample is analyzed for bacterial endotoxins using the Limulus Amebocyte Lysate (LAL) method.
Results are reported in EU/vial or EU/mg, as applicable.`,
    },
    {
      key: 'methods_metals',
      label: 'Heavy Metals / Elemental Impurities',
      standard: 'USP <233>',
      text: `2 mL of purified water is added to the lyophilized powder in the vial, and the contents are gently mixed until fully dissolved.
An aliquot is taken and diluted with deionized water or prepared according to the validated sample preparation procedure.
The prepared sample is analyzed by ICP-MS against certified reference standards.
Results are reported in ppm or ug/g, as applicable.`,
    },
    {
      key: 'disclaimer',
      label: 'Disclaimer / Legal Notice',
      standard: '',
      text: `The data presented in this certificate are based solely on the analysis of the sample received and tested.
The sample meets internal quality assurance criteria unless otherwise noted or flagged.
Methods shown reference current analytical SOPs and applicable USP chapters.
ND = Not Detected.
LOQ = Limit of Quantification.
LAL = Limulus Amebocyte Lysate.
ICP-MS = Inductively Coupled Plasma Mass Spectrometry.
All values are reported on a per-vial basis unless otherwise stated.
This certificate applies only to the portion of the sample tested. All analyses were performed in accordance with the applicable Quality Management System.`,
    },
    {
      key: 'ruo',
      label: 'RUO Statement',
      standard: 'Research Use Only',
      text: `For Research Use Only. Not intended for human or veterinary diagnostic, therapeutic, or clinical use.`,
    },
    {
      key: 'custom',
      label: 'Custom / Free text',
      standard: '',
      text: '',
    },
  ],
  de: [
    {
      key: 'methods_purity',
      label: 'Reinheit / Wirksamkeit / Identifizierung',
      standard: 'USP <621>',
      text: `2 mL gereinigtes Wasser wird zum lyophilisierten Pulver in der Ampulle gegeben und vorsichtig gemischt.
Eine Probe wird entnommen und auf ca. 500 mg/L verdunnt.
Die verdunnte Probe wird per HPLC-UV/VIS analysiert.
Die Gesamtflache aller Peaks wird berechnet und die Flache des Hauptpeaks durch die Gesamtflache dividiert.
Die Identifizierung erfolgt durch Retentionszeitvergleich mit einem Referenzstandard.`,
    },
    {
      key: 'methods_endotoxins',
      label: 'Endotoxine',
      standard: 'USP <85>',
      text: `2 mL endotoxinfreies Wasser wird zum lyophilisierten Pulver gegeben und vorsichtig gemischt.
Eine Probe wird entnommen und mit endotoxinfreiem Wasser verdunnt.
Die Probe wird mit der LAL-Methode auf Endotoxine analysiert.
Ergebnisse werden in EU/Vial oder EU/mg angegeben.`,
    },
    {
      key: 'methods_metals',
      label: 'Schwermetalle / Elementare Verunreinigungen',
      standard: 'USP <233>',
      text: `2 mL gereinigtes Wasser wird zum lyophilisierten Pulver gegeben und gemischt.
Eine Probe wird entnommen und mit deionisiertem Wasser verdunnt.
Die Probe wird per ICP-MS gegen zertifizierte Referenzstandards analysiert.
Ergebnisse werden in ppm oder ug/g angegeben.`,
    },
    {
      key: 'disclaimer',
      label: 'Haftungsausschluss',
      standard: '',
      text: `Die prasentierten Daten basieren ausschliesslich auf der Analyse der erhaltenen Probe.
Die Probe erfullt interne Qualitassicherungskriterien, sofern nicht anderweitig vermerkt.
ND = Nicht nachgewiesen. LOQ = Nachweisgrenze. LAL = Limulus-Amobozyten-Lysat.
Alle Werte werden pro Ampulle angegeben, sofern nicht anders angegeben.
Dieses Zertifikat gilt nur fur den getesteten Teil der Probe.`,
    },
    {
      key: 'ruo',
      label: 'RUO-Erklarung',
      standard: 'Nur fur Forschungszwecke',
      text: `Nur fur Forschungszwecke. Nicht fur den Einsatz in der Human- oder Veterinardiagnostik bestimmt.`,
    },
    { key: 'custom', label: 'Freier Text', standard: '', text: '' },
  ],
};

export default function RichTextEditor({ onChange, lang = 'en', initialHtml = '' }) {
  const de = lang === 'de';
  const editorRef = useRef(null);
  const [sections, setSections] = useState([]);
  const [initialized, setInitialized] = React.useState(false);
  const templates = PREDEFINED_SECTIONS[lang] || PREDEFINED_SECTIONS.en;

  // Pre-load existing HTML when editing an approved certificate
  React.useEffect(() => {
    if (initialHtml && editorRef.current && !initialized) {
      editorRef.current.innerHTML = initialHtml;
      setInitialized(true);
      onChange && onChange({ html: initialHtml, text: '', sections: [] });
    }
  }, [initialHtml, initialized]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    notifyChange();
  };

  const notifyChange = () => {
    const html  = editorRef.current?.innerHTML || '';
    // innerText captures everything including predefined blocks — 
    // we pass the full html and let pdfGenerator parse it cleanly
    onChange && onChange({ html, text: '', sections });
  };

  const insertSection = (section) => {
    if (sections.find(s => s.key === section.key)) return; // already added
    const newSections = [...sections, section];
    setSections(newSections);

    // Insert formatted content into editor
    const editor = editorRef.current;
    if (!editor) return;

    // Build HTML block
    const titleHtml = section.standard
      ? `<div style="display:flex;justify-content:space-between;border-bottom:1px solid #ccc;padding-bottom:4px;margin-bottom:8px">
           <strong>${section.label}</strong>
           <span style="color:#1565C0;font-weight:600">${section.standard}</span>
         </div>`
      : `<div style="border-bottom:1px solid #ccc;padding-bottom:4px;margin-bottom:8px"><strong>${section.label}</strong></div>`;

    const contentHtml = section.text
      ? section.text.split('\n').map(l => l.trim() ? `<p style="margin:0 0 4px;font-size:13px">${l}</p>` : '').join('')
      : `<p style="margin:0;font-size:13px;color:#888">${de ? 'Text hier eingeben...' : 'Enter text here...'}</p>`;

    const block = `<div style="margin-bottom:20px;padding:12px;border:1px solid #e0e0e0;border-radius:4px">
      ${titleHtml}${contentHtml}
    </div><p><br></p>`;

    editor.focus();
    document.execCommand('insertHTML', false, block);
    notifyChange();
  };

  const clearEditor = () => {
    if (editorRef.current) editorRef.current.innerHTML = '';
    setSections([]);
    onChange && onChange({ html: '', text: '', sections: [] });
  };

  return (
    <div style={rt.wrap}>
      {/* Predefined sections */}
      <div style={rt.sectionBar}>
        <span style={rt.sectionBarLabel}>{de ? 'Abschnitt einfügen:' : 'Insert section:'}</span>
        <div style={rt.sectionBtns}>
          {templates.map(tmpl => (
            <button
              key={tmpl.key}
              style={{ ...rt.sectionBtn, ...(sections.find(s => s.key === tmpl.key) ? rt.sectionBtnUsed : {}) }}
              onClick={() => insertSection(tmpl)}
              disabled={!!sections.find(s => s.key === tmpl.key)}
              title={tmpl.standard || ''}
            >
              {sections.find(s => s.key === tmpl.key) ? '✓ ' : '+ '}{tmpl.label}
            </button>
          ))}
        </div>
      </div>

      {/* Formatting toolbar */}
      <div style={rt.toolbar}>
        {[
          { icon: 'B', cmd: 'bold',          title: 'Bold',          style: { fontWeight: 800 } },
          { icon: 'I', cmd: 'italic',         title: 'Italic',        style: { fontStyle: 'italic' } },
          { icon: 'U', cmd: 'underline',      title: 'Underline',     style: { textDecoration: 'underline' } },
        ].map(btn => (
          <button key={btn.cmd} style={rt.toolBtn} onClick={() => exec(btn.cmd)} title={btn.title}>
            <span style={btn.style}>{btn.icon}</span>
          </button>
        ))}
        <div style={rt.toolDivider} />
        {[
          { icon: '≡', cmd: 'justifyLeft',   title: 'Align left' },
          { icon: '≡', cmd: 'justifyCenter', title: 'Center' },
          { icon: '≡', cmd: 'justifyRight',  title: 'Align right' },
        ].map((btn, i) => (
          <button key={i} style={rt.toolBtn} onClick={() => exec(btn.cmd)} title={btn.title}>{btn.icon}</button>
        ))}
        <div style={rt.toolDivider} />
        <button style={rt.toolBtn} onClick={() => exec('insertUnorderedList')} title="Bullet list">•</button>
        <button style={rt.toolBtn} onClick={() => exec('insertOrderedList')}   title="Numbered list">1.</button>
        <div style={rt.toolDivider} />
        <select style={rt.fontSizeSelect} onChange={e => exec('fontSize', e.target.value)} defaultValue="3">
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="4">Large</option>
          <option value="5">X-Large</option>
        </select>
        <div style={{ flex: 1 }} />
        <button style={rt.clearBtn} onClick={clearEditor}>{de ? 'Leeren' : 'Clear'}</button>
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        style={rt.editor}
        onInput={notifyChange}
        data-placeholder={de ? 'Beginnen Sie zu tippen oder fügen Sie einen Abschnitt ein...' : 'Start typing or insert a section above...'}
      />

      <div style={rt.hint}>
        {de
          ? '💡 Der Text erscheint auf Seite 3 des PDF-Zertifikats.'
          : '💡 This text will appear on Page 3 of the PDF certificate.'}
      </div>
    </div>
  );
}

const rt = {
  wrap:          { width: '100%' },
  sectionBar:    { marginBottom: 10 },
  sectionBarLabel:{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' },
  sectionBtns:   { display: 'flex', flexWrap: 'wrap', gap: 6 },
  sectionBtn:    { background: 'rgba(30,94,255,0.1)', border: '1px solid rgba(30,94,255,0.25)', color: '#4A9EFF', padding: '5px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 600, transition: 'all 0.15s' },
  sectionBtnUsed:{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', color: '#34D399', cursor: 'not-allowed', opacity: 0.7 },
  toolbar:       { display: 'flex', gap: 2, alignItems: 'center', background: '#1A2550', borderRadius: '8px 8px 0 0', padding: '6px 10px', flexWrap: 'wrap' },
  toolBtn:       { background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', width: 28, height: 28, borderRadius: 5, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' },
  toolDivider:   { width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 4px' },
  fontSizeSelect:{ background: '#0A1438', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 5, padding: '2px 6px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  clearBtn:      { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#F87171', padding: '3px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' },
  editor:        {
    minHeight: 280, background: '#fff', color: '#111', borderRadius: '0 0 8px 8px',
    padding: '16px', fontSize: 13, lineHeight: 1.7, outline: 'none',
    border: '1px solid rgba(255,255,255,0.08)', borderTop: 'none',
    fontFamily: 'Arial, sans-serif', overflowY: 'auto',
  },
  hint:          { fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 8 },
};
